import { getDB, STORE_NAME_EXPORT } from '../getDB';
import type { SuggestedApp } from '../types';
import * as Sentry from '@sentry/react';

/**
 * Insert a recordSuggestion
 * For "Shown" type, updates existing entry if one exists (only one "Shown" entry per slug+accountId)
 * For other types, always creates a new entry
 */
export async function recordSuggestion(
  suggestion: Pick<SuggestedApp, 'slug' | 'type' | 'accountId'>,
): Promise<SuggestedApp> {
  const { slug, type, accountId } = suggestion;

  try {
    const db = await getDB();
    const timestamp = new Date().toISOString();

    // For "Shown" type, check if an entry already exists and update it instead
    if (type === 'Shown') {
      const tx = db.transaction(STORE_NAME_EXPORT, 'readwrite');
      const store = tx.store;
      const slugIndex = store.index('by-slug');
      const allForSlug = await slugIndex.getAll(slug);

      // Find existing "Shown" entry for this slug and accountId
      const existing = allForSlug.find(
        s => s.type === 'Shown' && s.accountId === accountId,
      );

      if (existing) {
        // Update existing entry with new timestamp
        const updated: SuggestedApp = {
          ...existing,
          timestamp,
        };
        await store.put(updated);
        await tx.done;
        return updated;
      }

      await tx.done;
    }

    // Create new entry (for "Shown" when none exists, or for other types)
    const suggestion: SuggestedApp = {
      type,
      slug,
      timestamp,
      accountId,
    };
    const id = await db.add(STORE_NAME_EXPORT, suggestion);
    return { ...suggestion, id };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to record app suggestion:', error);
    Sentry.captureException(error);
    throw error;
  }
}
